import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import MeasuredBox from 'shared/components/ui/MeasuredBox';
import { getFailureSummary } from 'features/agent-analytics/components/failures/failuresApi';

/**
 * The dashboard above the Failures table, under the table's period and
 * filters: failures over time as stacked lines — split by category, tool or
 * agent — where they fall by category, error type or agent and by tool, and
 * the share of runs that failed. Counts come from the same clusters the table
 * lists, so they agree with it.
 */

const mix = (token, pct) => `color-mix(in srgb, var(${token}) ${pct}%, var(--surface-base))`;

/** The table's tag tones: broken (reds) · slow or throttled (ambers) · rejected (cool hues) · other (grey). */
export const CATEGORY_COLOR = {
  http_5xx: 'var(--feedback-error)',
  connection: mix('--feedback-error', 62),
  code: mix('--feedback-error', 36),
  timeout: 'var(--feedback-warning)',
  rate_limit: mix('--feedback-warning', 50),
  auth: 'var(--feedback-info)',
  validation: mix('--feedback-info', 55),
  not_found: 'var(--accent-llm)',
  refused: 'var(--accent-enterprise)',
  other: 'color-mix(in srgb, var(--text-muted) 60%, transparent)',
};
const colorOf = (key) => CATEGORY_COLOR[key] || CATEGORY_COLOR.other;

/** Tools and agents have no meaning in their colour: distinct hues, in rank order. */
export const NAMED_PALETTE = [
  'var(--feedback-info)',
  'var(--accent-llm)',
  'var(--feedback-warning)',
  'var(--feedback-success)',
  'var(--accent-enterprise)',
  'var(--feedback-error)',
  mix('--feedback-info', 50),
  mix('--feedback-warning', 50),
];
const namedColor = (line, i) => (line.other ? CATEGORY_COLOR.other : NAMED_PALETTE[i % NAMED_PALETTE.length]);

const SPLITS = [
  { id: 'category', label: 'Category' },
  { id: 'tool', label: 'Tool' },
  { id: 'agent', label: 'Agent' },
];

const BREAKDOWNS = [
  { id: 'category', label: 'Category' },
  { id: 'error_type', label: 'Error type' },
  { id: 'agent', label: 'Agent' },
];

/** The lines for a split: key, label and colour, largest first, and the rows they read. */
function linesFor(data, split) {
  if (split === 'category' || !data.split?.[split]) {
    return { rows: data.series, lines: data.by_category.map((c) => ({ key: c.key, label: c.label, color: colorOf(c.key) })) };
  }
  const { keys, rows } = data.split[split];
  return { rows, lines: keys.map((k, i) => ({ key: k.key, label: k.label, color: namedColor(k, i) })) };
}

const axis = { fontSize: 10, fill: 'var(--text-muted)' };
const DONUT_H = 132;
const RATE_H = 120;
/** Two donut cards high, so the time chart and the column beside it end together. */
const OVER_TIME_H = 236;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Day buckets are UTC dates; read them as dates, not shifted by the viewer's zone. */
const dayLabel = (t) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(t || ''));
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}` : String(t || '');
};
const hourLabel = (t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const hourTitle = (t) => new Date(t).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export function Card({ title, sub, action = null, className = '', children }) {
  return (
    <section className={`min-w-0 rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-sm ${className}`}>
      <header className="flex min-h-[32px] items-center gap-2 px-3 pt-2.5">
        <h3 className="truncate text-[12px] font-medium text-[color:var(--text-primary)]">{title}</h3>
        {sub ? <span className="truncate text-[11px] text-[color:var(--text-muted)]">{sub}</span> : null}
        {action}
      </header>
      <div className="px-3 pb-3 pt-1">{children}</div>
    </section>
  );
}

function SeriesTooltip({ active, payload, label, bucket, lines }) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.value > 0).reverse();
  if (!rows.length) return null;
  return (
    <div className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2.5 py-1.5 text-[11px]">
      <div className="mb-0.5 text-[color:var(--text-muted)]">{bucket === 'hour' ? hourTitle(label) : dayLabel(label)}</div>
      {rows.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-1.5 text-[color:var(--text-primary)]">
          <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: lines[p.dataKey]?.color }} />
          <span className="max-w-[180px] flex-1 truncate">{lines[p.dataKey]?.label || p.dataKey}</span>
          <span className="ml-3 tabular-nums">{p.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function OverTime({ data, split }) {
  const hourly = data.bucket === 'hour';
  const { rows, lines } = linesFor(data, split);
  const byKey = Object.fromEntries(lines.map((l) => [l.key, l]));
  return (
    <div className="flex flex-col gap-2">
      <div style={{ height: OVER_TIME_H }}>
        <MeasuredBox>
          {({ width, height }) => (
            <AreaChart width={width} height={height} data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
              <XAxis
                dataKey="t"
                tickFormatter={hourly ? hourLabel : dayLabel}
                tick={axis}
                axisLine={{ stroke: 'var(--border-default)' }}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis tick={axis} axisLine={false} tickLine={false} width={32} allowDecimals={false} />
              <Tooltip
                cursor={{ stroke: 'var(--border-strong)' }}
                content={<SeriesTooltip bucket={data.bucket} lines={byKey} />}
              />
              {/* Largest at the bottom of the stack. */}
              {lines.map((l) => (
                <Area
                  key={l.key}
                  type="monotone"
                  dataKey={l.key}
                  name={l.label}
                  stackId="f"
                  stroke={l.color}
                  strokeWidth={1.5}
                  fill={l.color}
                  fillOpacity={0.22}
                  isAnimationActive={false}
                />
              ))}
            </AreaChart>
          )}
        </MeasuredBox>
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-1">
        {lines.map((l) => (
          <li key={l.key} className="flex min-w-0 items-center gap-1.5 text-[11px] text-[color:var(--text-secondary)]">
            <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: l.color }} />
            <span className={`max-w-[160px] truncate ${split === 'tool' ? 'font-mono' : ''}`} title={l.label}>{l.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One donut and its legend: the parts, their counts and share of the whole. */
export function Donut({ items, total, mono = false }) {
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <div className="flex items-center gap-3" style={{ height: DONUT_H }}>
      <div className="relative h-[120px] w-[120px] shrink-0">
        <PieChart width={120} height={120}>
          <Pie
            data={items}
            dataKey="count"
            nameKey="label"
            innerRadius={38}
            outerRadius={58}
            paddingAngle={items.length > 1 ? 1 : 0}
            stroke="none"
            isAnimationActive={false}
          >
            {items.map((c) => <Cell key={c.key} fill={c.color} />)}
          </Pie>
        </PieChart>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-[15px] font-semibold tabular-nums text-[color:var(--text-primary)]">{total.toLocaleString()}</span>
        </div>
      </div>
      <ul className="max-h-full min-w-0 flex-1 space-y-0.5 overflow-hidden">
        {items.map((c) => (
          <li key={c.key} className="flex items-center gap-1.5 text-[11px] leading-4">
            <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: c.color }} />
            <span className={`min-w-0 flex-1 truncate ${c.other ? 'text-[color:var(--text-muted)]' : 'text-[color:var(--text-primary)]'} ${mono && !c.other ? 'font-mono' : ''}`} title={c.label}>{c.label}</span>
            <span className="tabular-nums text-[color:var(--text-primary)]">{c.count.toLocaleString()}</span>
            <span className="w-8 text-right tabular-nums text-[color:var(--text-muted)]">{pct(c.count)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const pctOf = (b) => (b.runs ? Math.round((b.failed / b.runs) * 1000) / 10 : null);

function RateTooltip({ active, payload, label, bucket }) {
  const b = active && payload?.[0]?.payload;
  if (!b || !b.runs) return null;
  return (
    <div className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2.5 py-1.5 text-[11px]">
      <div className="mb-0.5 text-[color:var(--text-muted)]">{bucket === 'hour' ? hourTitle(label) : dayLabel(label)}</div>
      <div className="tabular-nums text-[color:var(--text-primary)]">
        {b.pct}% · {b.failed.toLocaleString()} of {b.runs.toLocaleString()} runs
      </div>
    </div>
  );
}

/** Failed runs as a share of all runs, per bucket. */
function FailureRate({ rate, bucket }) {
  const hourly = bucket === 'hour';
  const rows = rate.series.map((b) => ({ ...b, pct: pctOf(b) }));
  return (
    <div style={{ height: RATE_H }}>
      <MeasuredBox>
        {({ width, height }) => (
          <AreaChart width={width} height={height} data={rows} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
            <XAxis
              dataKey="t"
              tickFormatter={hourly ? hourLabel : dayLabel}
              tick={axis}
              axisLine={{ stroke: 'var(--border-default)' }}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis tick={axis} axisLine={false} tickLine={false} width={32} unit="%" allowDecimals={false} />
            <Tooltip cursor={{ stroke: 'var(--border-strong)' }} content={<RateTooltip bucket={bucket} />} />
            <Area
              type="monotone"
              dataKey="pct"
              name="Failed runs"
              stroke="var(--feedback-error)"
              strokeWidth={1.5}
              fill="var(--feedback-error)"
              fillOpacity={0.15}
              connectNulls
              isAnimationActive={false}
            />
          </AreaChart>
        )}
      </MeasuredBox>
    </div>
  );
}

function Skeleton({ h }) {
  return <div className="animate-pulse rounded bg-surface-hover" style={{ height: h }} />;
}

/** Wide: the time chart on the left, two rows tall; the donuts stacked beside it. */
const GRID = 'grid shrink-0 grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3';
const SPAN = {
  time: 'md:col-span-2 lg:col-span-2 lg:row-span-2',
  categories: '',
  tools: '',
  rate: 'md:col-span-2 lg:col-span-3',
};
const SKELETON_H = { time: OVER_TIME_H, categories: DONUT_H, tools: DONUT_H, rate: RATE_H };

/** Named parts (tools, agents, error types) as donut items: top few, the rest as one. */
const namedItems = (list) => list.map((t, i) => ({
  key: t.other ? '__other' : t.name, label: t.other ? `${t.items} more` : t.name, count: t.count, other: Boolean(t.other), color: namedColor(t, i),
}));

function SplitToggle({ value, onChange, options = SPLITS, label = 'Split by' }) {
  return (
    <div className="ml-auto inline-flex shrink-0 overflow-hidden rounded-md border border-[color:var(--border-default)]" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={`h-6 px-2 text-[11px] transition-colors ${value === o.id
            ? 'bg-surface-hover font-medium text-[color:var(--text-primary)]'
            : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * @param {{ teamId, period: {preset, from, to}|string, filters: {agents, tools, categories}, search: string }} props
 *   The table's period and filters; each selection null = everything.
 */
export default function FailuresSummary({ teamId, period, filters = {}, search = '' }) {
  const q = useDebounced(search, 250);
  const periodKey = JSON.stringify(period ?? null);
  const filtersKey = JSON.stringify({ agents: filters.agents ?? null, tools: filters.tools ?? null, categories: filters.categories ?? null });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [split, setSplit] = useState('category');
  const [breakdown, setBreakdown] = useState('category');

  useEffect(() => {
    if (!teamId) return undefined;
    let live = true;
    setLoading(true);
    setError(null);
    getFailureSummary(teamId, { period: JSON.parse(periodKey), filters: { ...JSON.parse(filtersKey), search: q } })
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(e?.response?.data?.error || e?.message || 'Could not load failures'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [teamId, periodKey, filtersKey, q]);

  if (error && !data) return <div className="shrink-0 text-[12px] text-[color:var(--text-muted)]">{error}</div>;

  if (!data) {
    if (!loading) return null;
    return (
      <div className={GRID} data-testid="failures-summary-loading">
        {Object.entries(SPAN).map(([k, span]) => (
          <div key={k} className={`min-w-0 rounded-md border border-[color:var(--border-default)] bg-surface p-3 shadow-token-sm ${span}`}>
            <Skeleton h={SKELETON_H[k]} />
          </div>
        ))}
      </div>
    );
  }

  if (data.total === 0) return null;

  const parts = breakdown === 'error_type'
    ? namedItems(data.by_error_type)
    : breakdown === 'agent'
      ? namedItems(data.by_agent)
      : data.by_category.map((c) => ({ key: c.key, label: c.label, count: c.count, color: colorOf(c.key) }));
  const partsTotal = breakdown === 'category' ? data.total : parts.reduce((n, t) => n + t.count, 0);
  const tools = namedItems(data.by_tool);
  const toolTotal = tools.reduce((n, t) => n + t.count, 0);
  const rate = data.rate?.runs ? data.rate : null;

  return (
    <div className={`${GRID} transition-opacity ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
      <Card
        title="Over time"
        sub={data.bucket === 'hour' ? 'per hour' : 'per day'}
        action={<SplitToggle value={split} onChange={setSplit} />}
        className={SPAN.time}
      >
        <OverTime data={data} split={split} />
      </Card>
      <Card
        title="Breakdown"
        action={<SplitToggle value={breakdown} onChange={setBreakdown} options={BREAKDOWNS} label="Break down by" />}
        className={SPAN.categories}
      >
        {parts.length ? <Donut items={parts} total={partsTotal} mono={breakdown === 'error_type'} /> : (
          <div className="flex items-center justify-center text-[12px] text-[color:var(--text-muted)]" style={{ height: DONUT_H }}>None recorded</div>
        )}
      </Card>
      <Card title="Tools" sub={toolTotal < data.total ? `${(data.total - toolTotal).toLocaleString()} run-level not shown` : null} className={SPAN.tools}>
        {tools.length ? <Donut items={tools} total={toolTotal} mono /> : (
          <div className="flex items-center justify-center text-[12px] text-[color:var(--text-muted)]" style={{ height: DONUT_H }}>No tool failures</div>
        )}
      </Card>
      {rate ? (
        <Card
          title="Failed runs"
          sub={`${pctOf(rate)}% of ${rate.runs.toLocaleString()} runs`}
          className={SPAN.rate}
        >
          <FailureRate rate={rate} bucket={data.bucket} />
        </Card>
      ) : null}
    </div>
  );
}
